import React, { useState } from 'react';
import { Calendar as CalendarIcon, MapPin, User, Phone, Mail, MessageSquare, Package, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Calendar } from './ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { format } from 'date-fns';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const services = [
  ['house-removals', 'House & flat removals'],
  ['man-and-van', 'Man and van'],
  ['office-removals', 'Office removals'],
  ['furniture-delivery', 'Furniture delivery & collection'],
  ['rubbish-clearance', 'Rubbish & house clearance'],
  ['long-distance', 'Long-distance move'],
  ['other', 'Something else'],
];
const EMPTY = { name: '', phone: '', email: '', service: '', from: '', to: '', notes: '', website: '' };
const PHONE_OK = (v) => /^\+?[\d\s()-]{10,20}$/.test(v.trim()) && v.replace(/\D/g, '').length >= 10;

const QuoteForm = ({ compact = false }) => {
  const [form, setForm] = useState(EMPTY);
  const [date, setDate] = useState();
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(null);
  const [error, setError] = useState('');

  const handleChange = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError('');
    if (form.name.trim().length < 2 || !form.service) { setError('Please fill in your name and the service you need.'); return; }
    if (!PHONE_OK(form.phone)) { setError('Please enter a valid UK phone number so we can reach you.'); return; }
    if (form.email.trim() && !/^\S+@\S+\.\S+$/.test(form.email.trim())) { setError('Please check your email address, or leave it blank.'); return; }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/enquiry`, {
        name: form.name.trim(), phone: form.phone.trim(), email: form.email.trim() || null, service: form.service,
        from_area: form.from.trim(), to_area: form.to.trim(), notes: form.notes.trim(), website: form.website,
        date: date ? format(date, 'yyyy-MM-dd') : null,
      }, { timeout: 20000 });
      setSent(data.ref);
      setForm(EMPTY);
      setDate(undefined);
      toast.success('Thanks — your enquiry has reached us.');
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(err?.response?.status < 500 && typeof d === 'string' ? d : Array.isArray(d) ? `Please check: ${d.map(x => x.loc?.slice(-1)[0]).filter(Boolean).join(', ')}.` : `We couldn't send that just now. Please try again, or call ${'07985 291097'}.`);
    } finally { setSubmitting(false); }
  };

  if (sent) {
    return (
      <div className={`bg-white rounded-xl p-6 md:p-7 border border-slate-200 ${compact ? '' : 'max-w-xl'}`} data-testid="contact-success">
        <h3 className="font-display text-2xl font-extrabold text-[color:var(--mv-navy)]">Enquiry received</h3>
        <p className="text-slate-600 text-sm mt-2">Thanks — we've got your details (reference <strong>{sent}</strong>) and will get back to you as soon as we can. For anything urgent, call <a href="tel:07985291097" className="font-bold underline">07985 291097</a>.</p>
        <button type="button" onClick={() => setSent(null)} className="mt-4 text-sm font-bold text-[color:var(--mv-orange)]" data-testid="contact-send-another">Send another enquiry</button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate data-testid="contact-form" className={`bg-white rounded-xl p-6 md:p-7 border border-slate-200 shadow-[0_30px_60px_-30px_rgba(11,37,69,0.35)] ${compact ? '' : 'max-w-xl'}`}>
      <div className="mb-5">
        <div className="inline-block px-3 py-1 bg-orange-50 text-[color:var(--mv-orange)] rounded-full text-xs font-bold tracking-wider uppercase">Fast & Free</div>
        <h3 className="font-display text-3xl font-extrabold text-[color:var(--mv-navy)] mt-2">Get Your Free Quote</h3>
        <p className="text-slate-500 text-sm mt-1">Tell us about the job and we'll get back to you with a price. Want a price straight away? Use the instant quote on our home page.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field icon={User} placeholder="Your name" value={form.name} onChange={handleChange('name')} maxLength={80} autoComplete="name" data-testid="contact-name" />
        <Field icon={Phone} placeholder="Phone number" value={form.phone} onChange={handleChange('phone')} type="tel" maxLength={20} autoComplete="tel" data-testid="contact-phone" />
      </div>
      <div className="mt-3">
        <Field icon={Mail} placeholder="Email (optional)" value={form.email} onChange={handleChange('email')} type="email" maxLength={120} autoComplete="email" data-testid="contact-email" />
        <input type="text" name="website" value={form.website} onChange={handleChange('website')} tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
        <div className="relative">
          <Package className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 z-10" />
          <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
            <SelectTrigger className="pl-9 h-11" data-testid="contact-service">
              <SelectValue placeholder="Select service" />
            </SelectTrigger>
            <SelectContent>
              {services.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`contact-service-${v}`}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <Popover>
          <PopoverTrigger asChild>
            <button type="button" className="h-11 w-full rounded-md border border-slate-200 bg-white text-left pl-9 pr-3 text-sm text-slate-700 relative hover:border-slate-300">
              <CalendarIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              {date ? format(date, 'PPP') : <span className="text-slate-400">Preferred date</span>}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar mode="single" selected={date} onSelect={setDate} initialFocus />
          </PopoverContent>
        </Popover>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
        <Field icon={MapPin} placeholder="Collect from (postcode or town)" value={form.from} onChange={handleChange('from')} maxLength={80} data-testid="contact-from" />
        <Field icon={MapPin} placeholder="Deliver to (postcode or town)" value={form.to} onChange={handleChange('to')} maxLength={80} data-testid="contact-to" />
      </div>

      <div className="relative mt-3">
        <MessageSquare className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
        <textarea
          className="w-full rounded-md border border-slate-200 bg-white pl-9 pr-3 py-3 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[color:var(--mv-orange)] focus:border-transparent resize-none"
          rows={3}
          placeholder="Tell us about the job (items, floors, access…)"
          value={form.notes}
          maxLength={1000}
          onChange={handleChange('notes')}
          data-testid="contact-notes"
        />
      </div>

      {error && <div role="alert" className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="contact-error">{error}</div>}
      <button type="submit" disabled={submitting} className="btn-primary w-full justify-center mt-5 disabled:opacity-70" data-testid="contact-submit">
        <Send className="w-4 h-4" /> {submitting ? 'Sending…' : 'Get My Free Quote'}
      </button>
      <p className="text-xs text-slate-400 text-center mt-3">No obligation and free. We only use your details to reply about this job — see our <a href="/privacy" className="underline">privacy policy</a>.</p>
    </form>
  );
};

const Field = ({ icon: Icon, ...props }) => (
  <div className="relative">
    <Icon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
    <input
      {...props}
      className="h-11 w-full rounded-md border border-slate-200 bg-white pl-9 pr-3 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[color:var(--mv-orange)] focus:border-transparent"
    />
  </div>
);

export default QuoteForm;
